import auth from "services/auth";
import ls from "services/ls";
import config from "configs/config.js";
class Api {
  constructor() {
    this.apiurl = config.apiUrl;
    this._refreshPromise = null;
  }

  firstTouch(url) {
    if (url.indexOf("http") !== 0) {
      url = this.apiurl + url;
    }
    return url;
  }

  finalTouch(url) {
    if (config.debug) {
      return url + "?XDEBUG_SESSION_START=netbeans-xdebug";
    } else {
      return url;
    }
  }

  async attachAuthorizeHeader(headers) {
    if (typeof headers !== "object") {
      headers = {};
    }
    var accessToken = auth.getAccessToken();
    if (accessToken) {
      if (auth.isTokenExpired()) {
        console.log(
          "Access token present but expired. Need to refresh the token"
        );
        await this.refreshToken();
        accessToken = auth.getAccessToken();
      }
      headers.Authorization = "Bearer " + accessToken;
    }
    var language = ls.get("language");
    if (!language) {
      language = config.defaultLanguage;
    }
    headers["X-Request-Language"] = language.code;

    var geoexception = ls.get("geoexception");
    if (geoexception) {
      headers["X-GeoRestriction-Exception-Accepted"] = geoexception;
    }
    return headers;
  }

  parseParams(url, params) {
    url = this.firstTouch(url);
    let regexp;
    if (typeof params !== "object") {
      return url;
    }
    for (let [key, value] of Object.entries(params)) {
      if ((value || value === 0) && value.toString().length) {
        regexp = new RegExp("/:" + key + "(/|$)", "g");
        url = url.replace(regexp, "/" + value + "$1");
      }
    }
    //remove the trailing slash
    if (url.substr(-1, 1) == "/") {
      url = url.substr(0, url.length - 1);
    }

    //remove trailing variables if they are not already filled
    regexp = new RegExp("/:([^/])+($)", "g");
    while (url.match(regexp)) {
      url = url.replace(regexp, "");
    }
    //if any variable in the middle of url is not set, then replace with null
    regexp = new RegExp("/:([^/])+/", "g");
    while (url.match(regexp)) {
      url = url.replace(regexp, "/null/");
    }

    return url;
  }

  buildUrl(url, params) {
    if (!params || typeof params !== "object") {
      return url;
    }
    const [baseUrl, queryString] = url.split("?");
    const searchParams = new URLSearchParams(queryString || "");
    for (const [key, value] of Object.entries(params)) {
      if (value || value === 0) {
        searchParams.set(key, value);
      }
    }
    const finalQuery = searchParams.toString();
    return finalQuery ? `${baseUrl}?${finalQuery}` : baseUrl;
  }

  async parseResponse(response) {
    const contentType = response.headers.get("content-type") || "";
    if (contentType.includes("application/json")) {
      return await response.json();
    }
    const text = await response.text();
    return text ? text : null;
  }

  async request(url, options) {
    const response = await fetch(url, options);
    const data = await this.parseResponse(response);
    const wrapped = {
      status: response.status,
      data,
      headers: response.headers,
    };
    if (!response.ok) {
      throw { response: wrapped };
    }
    return wrapped;
  }

  async xhrPost(url, body, headers, onProgress) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", url, true);
      xhr.withCredentials = true;
      if (headers && typeof headers === "object") {
        Object.entries(headers).forEach(([key, value]) => {
          xhr.setRequestHeader(key, value);
        });
      }
      if (xhr.upload && onProgress) {
        xhr.upload.onprogress = onProgress;
      }
      xhr.onreadystatechange = () => {
        if (xhr.readyState !== 4) {
          return;
        }
        let data = null;
        try {
          data = xhr.responseText ? JSON.parse(xhr.responseText) : null;
        } catch (error) {
          data = xhr.responseText;
        }
        const wrapped = {
          status: xhr.status,
          data,
          headers: xhr.getAllResponseHeaders(),
        };
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(wrapped);
        } else {
          reject({ response: wrapped });
        }
      };
      xhr.onerror = () => {
        reject({});
      };
      xhr.send(body);
    });
  }

  async get(url, getParams, headers, retrying) {
    if (retrying === undefined) {
      retrying = false;
    }
    try {
      headers = await this.attachAuthorizeHeader(headers);
      url = this.firstTouch(url);
      url = this.finalTouch(url);
      url = this.buildUrl(url, getParams);
      const response = await this.request(url, {
        method: "GET",
        headers,
        credentials: "include",
      });
      return response;
    } catch (error) {
      if (!retrying && error.response && error.response.status === 401) {
        await this.refreshToken();
        return await this.get(url, getParams, headers, true);
      }
      return this.formatErrorResponse(error);
    }
  }
  //
  async post(url, postData, headers, onProgress, getParams, retrying) {
    if (retrying === undefined) {
      retrying = false;
    }
    try {
      headers = await this.attachAuthorizeHeader(headers);
      url = this.firstTouch(url);
      url = this.finalTouch(url);
      url = this.buildUrl(url, getParams);
      let body = postData;
      const isFormData = typeof FormData !== "undefined" && postData instanceof FormData;
      const isString = typeof postData === "string";
      if (!isFormData && !isString && postData !== null && postData !== undefined) {
        headers = { ...headers, "Content-Type": "application/json" };
        body = JSON.stringify(postData);
      }
      const response = onProgress
        ? await this.xhrPost(url, body, headers, onProgress)
        : await this.request(url, {
            method: "POST",
            headers,
            body,
            credentials: "include",
          });
      return response;
    } catch (error) {
      if (!retrying && error.response && error.response.status === 401) {
        await this.refreshToken();
        return await this.post(
          url,
          postData,
          headers,
          onProgress,
          getParams,
          true
        );
      }
      return this.formatErrorResponse(error);
    }
  }

  async download(url) {
    try {
      var accessToken = auth.getAccessToken();
      if (accessToken) {
        if (auth.isTokenExpired()) {
          console.log(
            "Access token present but expired. Need to refresh the token"
          );
          await this.refreshToken();
          accessToken = auth.getAccessToken();
        }
      }
      if (accessToken) {
        url = this.firstTouch(url);
        url = this.finalTouch(url);
        if (url.indexOf("?") !== -1) {
          url = url + "&rnd=" + accessToken;
        } else {
          url = url + "?rnd=" + accessToken;
        }
        var newWindow = window.open(url, "_blank");
        // newWindow.close();
        return true;
      }
    } catch (error) {
      return error;
    }
  }

  formatErrorResponse(error) {
    if (error.response) {
      return error.response;
    } else {
      return {
        status: 503,
        data: {
          error: "Service unavailable",
          details: "The API server is not reachable or not responding",
        },
      };
    }
  }

  async refreshToken() {
    if (this._refreshPromise) {
      return this._refreshPromise;
    }

    this._refreshPromise = this._doRefreshToken();

    try {
      return await this._refreshPromise;
    } finally {
      this._refreshPromise = null;
    }
  }

  async _doRefreshToken() {
    try {
      var url = "/refresh_token";
      url = this.firstTouch(url);
      url = this.finalTouch(url);
      const response = await this.request(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
        credentials: "include",
      });
      if (response.status === 200) {
        auth.login(response.data);
        return true;
      } else if (response.status === 401) {
        console.log("Refresh token invalid or expired");
        auth.logout();
        return false;
      }
    } catch (error) {
      console.log("Error refreshing token");
      console.log(error);
      if (error?.response?.status === 401) {
        auth.logout();
      }
      return false;
    }
  }
}

export default new Api();
